import { useEffect, useRef } from 'react';
import { Calendar, ArrowRight, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

export const blogPosts = [
  {
    id: 1,
    slug: 'construccion-sustentable-sostenible',
    image: 'https://customer-assets.emergentagent.com/job_desarrollo-faraci/artifacts/c1urk5h2_Imagen%20ART1.webp',
    category: 'Sostenibilidad',
    title: 'Edificaciones Faraci: Impulsando la construcción sustentable y sostenible en nuestro estado',
    excerpt: 'En Faraci Edificaciones asumimos un rol de liderazgo en la transformación del sector de la construcción hacia prácticas más sustentables y sostenibles.',
    content: `En Faraci Edificaciones asumimos un rol de liderazgo en la transformación del sector de la construcción hacia prácticas más sustentables y sostenibles. Entendemos la creciente importancia de minimizar el impacto ambiental de nuestras edificaciones y, por ello, incorporamos criterios de sostenibilidad desde la concepción de cada proyecto. Buscamos activamente la implementación de materiales de construcción con menor huella de carbono, así como el diseño de espacios que optimicen el uso de recursos naturales como la luz y el agua, contribuyendo a la preservación del entorno jalisciense.

Nuestro compromiso se extiende a la adopción de técnicas constructivas innovadoras que favorecen la eficiencia energética y la reducción de residuos en nuestras obra. En cada proyecto, analizamos el ciclo de vida de los materiales y las soluciones implementadas, buscando alternativas que no solo cumplan con los más altos estándares de calidad, sino que también promuevan un desarrollo urbano más consciente y respetuoso con el medio ambiente de Jalisco.

En Faraci Edificaciones, creemos que la construcción sostenible no es solo una tendencia, sino una responsabilidad fundamental. Nuestros proyectos buscan ser un referente en Jalisco, demostrando que es posible edificar espacios funcionales, duraderos y estéticamente atractivos, al mismo tiempo que se contribuye a la creación de un futuro más verde y resiliente para nuestra comunidad.`,
    date: '15 Dic 2025',
    readTime: '5 min',
  },
  {
    id: 2,
    slug: 'compromiso-calidad-proyectos',
    image: 'https://customer-assets.emergentagent.com/job_desarrollo-faraci/artifacts/0xwuee2t_Imagen%20ART2.webp',
    category: 'Calidad',
    title: 'Edificaciones Faraci: Nuestro compromiso con la calidad y nuestros proyectos de construcción',
    excerpt: 'Nuestro compromiso con la calidad no es una promesa, sino el cimiento sobre el que se levanta cada uno de nuestros proyectos.',
    content: `Nuestro compromiso con la calidad no es una promesa, sino el cimiento sobre el que se levanta cada uno de nuestros proyectos. Desde la etapa de cimentación, utilizando la experticia de nuestra ingeniería civil, hasta la meticulosa selección y aplicación de los acabados más finos, aplicamos un rigor técnico inquebrantable. Esta dedicación nos permite superar los estándares de la industria y garantizar la durabilidad y funcionalidad de cada inmueble, ya sean viviendas, naves industriales u obras de infraestructura mayor.

Nuestra gestión integral de proyectos es una manifestación directa de este compromiso. A través de una supervisión detallada y una administración eficiente, aseguramos que cada etapa de la construcción se complete con precisión. Esto se traduce en un control presupuestal estricto, una optimización de los tiempos de entrega y una minimización de riesgos, lo que nos permite mantener la transparencia y construir relaciones de confianza duraderas con nuestros socios y clientes.

La calidad en Faraci Edificaciones es el resultado de un proceso holístico que integra la excelencia técnica, la eficiencia operativa y una comunicación abierta. Creemos firmemente que cada obra que entregamos debe ser un reflejo de nuestros valores, aportando espacios dignos que contribuyan de forma positiva al desarrollo comunitario en nuestro mercado meta en Jalisco y en México.`,
    date: '10 Dic 2025',
    readTime: '6 min',
  },
  {
    id: 3,
    slug: 'nuevas-tecnologias-construccion-mexico',
    image: 'https://customer-assets.emergentagent.com/job_desarrollo-faraci/artifacts/g0dyzjvp_Imagen%20ART3.webp',
    category: 'Tecnología',
    title: 'Edificaciones Faraci: El uso de nuevas tecnologías en los procesos de construcción en México',
    excerpt: 'En Edificaciones Faraci, reconocemos que el futuro de la construcción en México está intrínsecamente ligado a la innovación tecnológica.',
    content: `En Edificaciones Faraci, reconocemos que el futuro de la construcción en México está intrínsecamente ligado a la innovación tecnológica. Hemos integrado de manera estratégica herramientas de vanguardia para optimizar cada etapa de nuestros proyectos. Desde el uso de drones para levantamientos topográficos y monitoreo de obra en tiempo real, hasta la implementación de software de modelado de información de construcción (BIM) que nos permite visualizar proyectos en 3D, nuestra meta es maximizar la eficiencia y precisión en la ejecución.

La adopción de estas tecnologías nos permite ofrecer una gestión más transparente y una comunicación más fluida con nuestros clientes. A través de plataformas digitales, los socios pueden supervisar el avance de sus proyectos, revisar presupuestos y tomar decisiones informadas, todo con una visibilidad sin precedentes. Esta apertura, combinada con la reducción de errores y la optimización de recursos, refuerza nuestra promesa de calidad y satisfacción del cliente.

Estamos comprometidos a seguir invirtiendo en la capacitación de nuestro equipo y en la incorporación de nuevas herramientas que nos permitan mantenernos a la vanguardia del sector. En Faraci Edificaciones, no solo construimos inmuebles; construimos un futuro más inteligente y eficiente para la industria de la construcción en México, demostrando que la tecnología es un pilar fundamental para el desarrollo sostenible y la excelencia operativa.`,
    date: '5 Dic 2025',
    readTime: '5 min',
  },
];

export const BlogSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="blog"
      ref={sectionRef}
      data-testid="blog-section"
      className="py-20 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 reveal">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" />
              <span className="font-manrope uppercase tracking-widest text-xs font-bold text-[#BF9000]">
                Noticias y Artículos
              </span>
            </div>
            <h2
              data-testid="blog-title"
              className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-bold text-[#385723] uppercase tracking-tight"
            >
              Nuestro <span className="text-[#92D050]">Blog</span>
            </h2>
          </div>
          <Button
            data-testid="blog-view-all"
            variant="outline"
            className="mt-6 md:mt-0 border-2 border-[#385723] text-[#385723] hover:bg-[#385723] hover:text-white font-oswald uppercase tracking-wider px-6"
          >
            Ver Todos los Artículos
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <Link
              to={`/blog/${post.slug}`}
              key={post.id}
              data-testid={`blog-card-${post.id}`}
              className="blog-card bg-white group cursor-pointer reveal block"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <article>
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#92D050] text-[#385723] font-manrope text-xs font-bold uppercase tracking-wider px-3 py-1">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center gap-4 mb-4 text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span className="font-manrope text-xs">{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-manrope text-xs">{post.readTime}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-oswald text-xl font-bold text-[#385723] uppercase tracking-tight mb-3 group-hover:text-[#92D050] transition-colors duration-300 line-clamp-2">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="font-manrope text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Read More */}
                  <div className="flex items-center text-[#385723] font-manrope font-semibold text-sm group-hover:text-[#92D050] transition-colors duration-300">
                    Leer más
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
